import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { createShortage } from "../../api/penalties";
import "./HrShortageManager.css";

const SCOPE_TYPES = [
  { value: "branch", label: "فرع" },
  { value: "department", label: "إدارة غير تشغيل" },
  { value: "cashiers", label: "كاشيرية فرع" },
];

const CASHIER_TITLES = new Set(["كاشير", "مدير الكاشيرية"]);
const SHORTAGE_PREFIX = "عجز:";

function isShortagePenalty(pen) {
  return String(pen?.reason || "").trim().startsWith(SHORTAGE_PREFIX);
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("ar-EG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function calcShares(totalAmount, count) {
  if (!count || count < 1) return [];
  const totalCents = Math.round(Number(totalAmount) * 100);
  if (!Number.isFinite(totalCents) || totalCents < 1) return [];
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;
  return Array.from({ length: count }, (_, i) =>
    (i === 0 ? base + remainder : base) / 100,
  );
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}

function HrShortageManager({ userRole }) {
  const {
    employeesByBranch,
    employeePenalties,
    branches,
    departments,
    refreshData,
    addRecentActivity,
  } = useData();

  const canCreate = userRole === "hr" || userRole === "hr-staff" || userRole === "admin";

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [scopeType, setScopeType] = useState("branch");
  const [scopeId, setScopeId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalAmount, setTotalAmount] = useState("");
  const [reason, setReason] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [empSearch, setEmpSearch] = useState("");

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e.status !== "terminated"),
    [employeesByBranch],
  );

  const nonOpsDepartments = useMemo(
    () =>
      (departments || []).filter(
        (d) =>
          !d.isOperational &&
          d.code !== "OPS" &&
          d.id !== "operations",
      ),
    [departments],
  );

  const activeBranches = useMemo(
    () => (branches || []).filter((b) => b.isActive !== false),
    [branches],
  );

  const scopedEmployees = useMemo(() => {
    if (!scopeId) return [];
    if (scopeType === "branch") {
      return allEmployees.filter(
        (e) => String(e.branchBackendId || e.branchId) === String(scopeId),
      );
    }
    if (scopeType === "cashiers") {
      return allEmployees.filter(
        (e) =>
          String(e.branchBackendId || e.branchId) === String(scopeId) &&
          CASHIER_TITLES.has(e.jobTitle),
      );
    }
    if (scopeType === "department") {
      return allEmployees.filter(
        (e) =>
          String(e.departmentBackendId || e.departmentId) === String(scopeId),
      );
    }
    return [];
  }, [allEmployees, scopeId, scopeType]);

  const filteredScopedEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return scopedEmployees;
    return scopedEmployees.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.code?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q),
    );
  }, [scopedEmployees, empSearch]);

  const sharesPreview = useMemo(() => {
    const amount = Number(totalAmount);
    if (!selectedIds.length || !Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    const shares = calcShares(amount, selectedIds.length);
    const equal =
      shares.length > 0 && shares.every((s) => s === shares[0]);
    return {
      count: selectedIds.length,
      perPerson: shares[0],
      firstPerson: shares[0],
      equal,
      shares,
    };
  }, [totalAmount, selectedIds]);

  const shortagePenalties = useMemo(
    () => (employeePenalties || []).filter(isShortagePenalty),
    [employeePenalties],
  );

  const filteredPenalties = useMemo(() => {
    let list = shortagePenalties;
    if (filter !== "all") {
      list = list.filter((p) => p.status === filter);
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const emp = allEmployees.find(
          (e) => String(e.id) === String(p.employeeId),
        );
        const name = (p.employeeName || emp?.name || "").toLowerCase();
        return (
          name.includes(q) ||
          String(p.reason || "")
            .toLowerCase()
            .includes(q) ||
          String(p.employeeCode || emp?.code || "")
            .toLowerCase()
            .includes(q)
        );
      });
    }
    return list;
  }, [shortagePenalties, filter, searchTerm, allEmployees]);

  function getEmployeeName(employeeId) {
    const emp = allEmployees.find((e) => String(e.id) === String(employeeId));
    if (emp?.name) return emp.name;
    const fromPen = shortagePenalties.find(
      (p) => String(p.employeeId) === String(employeeId),
    );
    return fromPen?.employeeName || "غير معروف";
  }

  function getEmployeeCode(employeeId) {
    const emp = allEmployees.find((e) => String(e.id) === String(employeeId));
    if (emp?.code) return emp.code;
    const fromPen = shortagePenalties.find(
      (p) => String(p.employeeId) === String(employeeId),
    );
    return fromPen?.employeeCode || "";
  }

  function openAddModal() {
    setScopeType("branch");
    setScopeId("");
    setSelectedIds([]);
    setTotalAmount("");
    setReason("");
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setEmpSearch("");
    setError("");
    setShowAddModal(true);
  }

  function handleScopeTypeChange(next) {
    setScopeType(next);
    setScopeId("");
    setSelectedIds([]);
    setEmpSearch("");
  }

  function handleScopeIdChange(next) {
    setScopeId(next);
    setSelectedIds([]);
  }

  function toggleEmployee(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectAllVisible() {
    const ids = filteredScopedEmployees.map((e) => e.id);
    setSelectedIds((prev) => {
      const set = new Set(prev);
      ids.forEach((id) => set.add(id));
      return [...set];
    });
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!canCreate) {
      setError("تسجيل العجز متاح للموارد البشرية فقط");
      return;
    }
    if (!selectedIds.length) {
      setError("اختر موظفاً واحداً على الأقل");
      return;
    }
    const amount = Number(totalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("أدخل مبلغ العجز الإجمالي");
      return;
    }
    if (!reason.trim()) {
      setError("أدخل سبب العجز");
      return;
    }
    if (!effectiveDate) {
      setError("اختر تاريخ السريان");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        employeeIds: selectedIds,
        totalAmount: amount,
        reason: reason.trim(),
        effectiveDate,
      };
      if (scopeType === "branch" || scopeType === "cashiers") {
        payload.branchId = scopeId || undefined;
      }
      if (scopeType === "department") {
        payload.departmentId = scopeId || undefined;
      }

      const created = await createShortage(payload);
      const count = Array.isArray(created) ? created.length : selectedIds.length;

      addRecentActivity?.({
        type: "shortage",
        title: "تسجيل عجز",
        description: `تم توزيع عجز ${formatMoney(amount)} جنيه على ${count} موظف`,
      });

      await refreshData?.();
      setShowAddModal(false);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "تعذر تسجيل العجز";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  function getStatusBadge(pen) {
    if (pen.status === "appealed")
      return (
        <span className="shortage-status-badge review">
          {pen.appealStatus || "معلق"}
        </span>
      );
    if (pen.status === "active")
      return <span className="shortage-status-badge active">ساري</span>;
    return <span className="shortage-status-badge cancelled">ملغي</span>;
  }

  const scopeOptions =
    scopeType === "department" ? nonOpsDepartments : activeBranches;

  return (
    <div className="shortage-manager" dir="rtl">
      <div className="shortage-header">
        <div>
          <h2 className="shortage-title">إدارة العجز</h2>
          <p className="shortage-subtitle">
            {shortagePenalties.length} سجل عجز |{" "}
            {shortagePenalties.filter((p) => p.status === "active").length} ساري
          </p>
        </div>
        {canCreate && (
          <button type="button" className="shortage-add-btn" onClick={openAddModal}>
            + تسجيل عجز
          </button>
        )}
      </div>

      <div className="shortage-filters">
        <button
          type="button"
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل
        </button>
        <button
          type="button"
          className={`filter-btn ${filter === "active" ? "active" : ""}`}
          onClick={() => setFilter("active")}
        >
          ساري
        </button>
        <button
          type="button"
          className={`filter-btn ${filter === "appealed" ? "active" : ""}`}
          onClick={() => setFilter("appealed")}
        >
          قيد المراجعة
        </button>
      </div>

      <div className="shortage-search">
        <input
          type="text"
          placeholder="بحث بالموظف أو السبب..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="shortage-search-input"
        />
      </div>

      <div className="shortage-list">
        {filteredPenalties.length === 0 ? (
          <div className="shortage-empty">
            <p>لا توجد سجلات عجز حالياً</p>
          </div>
        ) : (
          filteredPenalties.map((pen) => (
            <div key={pen.id} className="shortage-card">
              <div className="shortage-card-header">
                <div className="shortage-employee">
                  <div className="shortage-avatar">
                    {getEmployeeName(pen.employeeId)?.charAt(0)}
                  </div>
                  <div>
                    <div className="shortage-employee-name">
                      {getEmployeeName(pen.employeeId)}
                    </div>
                    <div className="shortage-employee-code">
                      {getEmployeeCode(pen.employeeId)}
                    </div>
                  </div>
                </div>
                {getStatusBadge(pen)}
              </div>

              <div className="shortage-details">
                <div className="shortage-detail-row">
                  <span className="shortage-detail-label">المبلغ:</span>
                  <span className="shortage-detail-value">
                    {formatMoney(pen.amount)} جنيه
                  </span>
                </div>
                <div className="shortage-detail-row">
                  <span className="shortage-detail-label">التاريخ:</span>
                  <span className="shortage-detail-value">
                    {formatDate(pen.effectiveDate || pen.date)}
                  </span>
                </div>
                <div className="shortage-detail-row">
                  <span className="shortage-detail-label">السبب:</span>
                  <span className="shortage-detail-value">{pen.reason}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div
          className="shortage-modal-overlay"
          onClick={() => !submitting && setShowAddModal(false)}
        >
          <div className="shortage-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="shortage-modal-title">تسجيل عجز</h3>
            <p className="shortage-modal-subtitle">
              اختر النطاق والموظفين — يُوزَّع المبلغ بالتساوي ويُخصم في المرتب
            </p>

            <form onSubmit={handleSubmit}>
              <div className="shortage-modal-field">
                <label className="shortage-modal-label">نوع النطاق *</label>
                <div className="shortage-scope-tabs">
                  {SCOPE_TYPES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className={`shortage-scope-tab ${
                        scopeType === s.value ? "active" : ""
                      }`}
                      onClick={() => handleScopeTypeChange(s.value)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="shortage-modal-field">
                <label className="shortage-modal-label">
                  {scopeType === "department" ? "الإدارة *" : "الفرع *"}
                </label>
                <select
                  className="shortage-modal-select"
                  value={scopeId}
                  onChange={(e) => handleScopeIdChange(e.target.value)}
                  required
                >
                  <option value="">
                    {scopeType === "department" ? "اختر الإدارة" : "اختر الفرع"}
                  </option>
                  {scopeOptions.map((opt) => (
                    <option
                      key={opt.backendId || opt.id}
                      value={opt.backendId || opt.id}
                    >
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>

              {scopeId && (
                <div className="shortage-modal-field">
                  <div className="shortage-emp-toolbar">
                    <label className="shortage-modal-label">
                      الموظفون ({selectedIds.length} مختار)
                    </label>
                    <div className="shortage-emp-actions">
                      <button
                        type="button"
                        className="shortage-link-btn"
                        onClick={selectAllVisible}
                      >
                        تحديد الكل
                      </button>
                      <button
                        type="button"
                        className="shortage-link-btn"
                        onClick={clearSelection}
                      >
                        إلغاء التحديد
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    className="shortage-modal-input"
                    placeholder="بحث داخل القائمة..."
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                  />
                  <div className="shortage-emp-list">
                    {filteredScopedEmployees.length === 0 ? (
                      <p className="shortage-modal-hint">
                        لا يوجد موظفون في هذا النطاق.
                      </p>
                    ) : (
                      filteredScopedEmployees.map((emp) => (
                        <label key={emp.id} className="shortage-emp-row">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(emp.id)}
                            onChange={() => toggleEmployee(emp.id)}
                          />
                          <span className="shortage-emp-info">
                            <strong>{emp.name}</strong>
                            <span>
                              {emp.code} — {emp.jobTitle || "—"}
                            </span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="shortage-modal-row">
                <div className="shortage-modal-field">
                  <label className="shortage-modal-label">
                    المبلغ الإجمالي (جنيه) *
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="shortage-modal-input"
                    value={totalAmount}
                    onChange={(e) => setTotalAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="shortage-modal-field">
                  <label className="shortage-modal-label">تاريخ السريان *</label>
                  <input
                    type="date"
                    className="shortage-modal-input"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              {sharesPreview && (
                <div className="shortage-preview">
                  {sharesPreview.equal ? (
                    <>
                      نصيب كل موظف من {sharesPreview.count}:{" "}
                      <strong>
                        {formatMoney(sharesPreview.perPerson)} جنيه
                      </strong>
                    </>
                  ) : (
                    <>
                      توزيع تقريبي على {sharesPreview.count} موظف — أول موظف:{" "}
                      <strong>
                        {formatMoney(sharesPreview.firstPerson)} جنيه
                      </strong>
                      ، والباقي{" "}
                      <strong>
                        {formatMoney(sharesPreview.shares[1])} جنيه
                      </strong>
                    </>
                  )}
                </div>
              )}

              <div className="shortage-modal-field">
                <label className="shortage-modal-label">السبب *</label>
                <textarea
                  className="shortage-modal-textarea"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="مثال: عجز صندوق الفرع لشهر يوليو"
                  required
                />
              </div>

              <div className="shortage-modal-note">
                يُسجَّل كخصم مالي ويُخصم تلقائياً في مرتبات الشهر حسب تاريخ
                السريان. يُرسل إشعار للموظفين وللمدير العام.
              </div>

              {error && <p className="shortage-modal-error">{error}</p>}

              <div className="shortage-modal-buttons">
                <button
                  type="button"
                  className="shortage-modal-btn secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="shortage-modal-btn primary"
                  disabled={submitting}
                >
                  {submitting ? "جاري التسجيل..." : "تسجيل العجز"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HrShortageManager;
